import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { AddonPermissionService } from 'app/addon-permission/addon-permission.service';
import { GlobalService } from 'app/global/global.service';
import { organizationFields, personFields } from 'app/global/recipientfields';
import { MomentPipe } from 'app/pipes/moment.pipe';
import { lastValueFrom, Observable } from 'rxjs';
import { IExtraFiledFormInterface } from '../../../global/extra-filed-form.interface';
import { UserTimezone } from '../../signing-pages/signing-page/signing-page.dto';
import { DocumentsService } from '../documents/documents.service';
import { IDocumentDetailRootDTO } from '../documents/dto/document-details.dto';
import { ConfirmationTypeEnum, InvitationThroughEnum } from '../general-editor-tabs/recipients-tab/recipient-tab.dto';
import { TemplateType } from '../templates/template.dto';
import { LogoTypeEnum, Organization, Orgfield, Personfield, Recipient, RecipientRole, Steps, TemplateForm } from './edit-template.dto';
import { EditTemplateService } from './edit-template.service';
import { steps } from './eform-steps.dto';

@Injectable({
    providedIn: 'root'
})

export class HelperService {

    private renderer: Renderer2;
    private templateForm: TemplateForm;
    private DK: boolean;
    public systemCurrency: string;
    constructor(
        private editTemplateService: EditTemplateService,
        private documentsService: DocumentsService,
        private formBuilder: FormBuilder,
        private globalService: GlobalService,
        private _dateFormatPipe$: MomentPipe,
        private addonPermissionService$: AddonPermissionService,
        private translocoService$: TranslocoService,
        private rendererFactory: RendererFactory2,
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
        this.addonPermissionService$.getAddonPermission('DK').subscribe({
            next: (enable: boolean) => {
                this.DK = enable;
            }
        });
        this.systemCurrency = this.globalService.getSystemCurrency();
    }



    
    /**
     * Initializes the API call based on the provided route and id.
     *
     * @param {string} route - The route to determine which service to call. Expected values are 'send-document' or 'edit-template'.
     * @param {string} id - The identifier to be used in the API call.
     * @returns {Observable<IDocumentDetailRootDTO | TemplateForm>} - An observable that emits the result of the API call.
     */
    initApi(route: string, id: string): Observable<IDocumentDetailRootDTO | TemplateForm> {
        const routeConfig = {
            'send-document': { service: this.documentsService, method: 'getDocument' },
            'edit-template': { service: this.editTemplateService, method: 'getTemplate' }
        };
        if (routeConfig[route]) {
            const { service, method } = routeConfig[route];
            return service[method](Number(id));
        }        
    }




    
    
    /**
     * Initializes the form based on the provided route and data.
     *
     * @param {string} route - The route to determine which form to initialize. Expected values are 'send-document' or 'edit-template'.
     * @param {TemplateForm} data - The TemplateForm data to be used in the form initialization.
     * @returns {Observable<any> | null} - An observable that emits the result of the form initialization, or null if no form was initialized.
     */
    initForm(route: string, data: TemplateForm) {
        const routeActions = {
            'send-document': this.prepairSendDocumentForm,
            'edit-template': this.prepairTemplateForm
        };
        return routeActions[route] ? routeActions[route].call(this, data) : null;
    }




    /**
     * Merges current form values into the template data object.
     * This ensures that unsaved user edits (Title, Description, BCC, Recipients, etc.)
     * are preserved when the form needs to be structuraly re-initialized (e.g., on document type change).
     *
     * @param template - The new template data from the store.
     * @param form - The current FormGroup containing unsaved user edits.
     * @returns The merged TemplateForm object.
     */
    mergeTemplateDataWithForm(template: TemplateForm, form: FormGroup): TemplateForm {
        if (!form) return template;

        const merged = { ...template };
        const currentValues = form.getRawValue();

        // Merge structural sections
        merged.template_details = { ...merged.template_details, ...currentValues.template_details };
        merged.bcc = { ...merged.bcc, ...currentValues.bcc };
        merged.template_settings = { ...merged.template_settings, ...currentValues.template_settings };
        merged.convert_to_eform_settings = { ...merged.convert_to_eform_settings, ...currentValues.convert_to_eform_settings };
        merged.template_roles = currentValues.template_roles || merged.template_roles;
        merged.custom_fields = currentValues.custom_fields || merged.custom_fields;

        if (currentValues.template) {
            merged.template = { ...merged.template, ...currentValues.template };
        }

        // Merge fields data to avoid losing document field selections
        if (merged.template_fields_datas) {
            merged.template_fields_datas.data = {
                ...(merged.template_fields_datas.data || {}),
                ...(this.globalService.fields_data || {})
            };
        }

        return merged;
    }


    /**
     * Prepares the template form with the provided data.
     *
     * @param data - The data to populate the template form.
     * @returns A FormGroup representing the template form.
     *
     * The form structure includes:
     * - `template`: A FormGroup containing template details such as id, name, and content.
     * - `template_details`: A FormGroup containing detailed settings for the template, including messages, language, deadlines, and more.
     * - `template_settings`: A FormGroup containing settings related to eForm conversion, sharing, and description.
     * - `bcc`: A FormGroup containing BCC email and subject settings.
     * - `convert_to_eform_settings`: A FormGroup containing settings for converting the template to an eForm, including redirect URL, participant, background, arrows, users, and more.
     * - `template_roles`: A FormArray containing roles associated with the template.
     * - `deleted_roles`: A FormArray for tracking deleted roles.
     * - `deleted_extra_fields`: A FormArray for tracking deleted extra fields.
     */
    private prepairTemplateForm(data: TemplateForm) {
        this.templateForm = data;
        return this.formBuilder.group({
            template: this.formBuilder.group({
                id: [data.template.id ?? 0],
                name: [data.template.name ?? '', [Validators.required]],
                content: [data.template.content ?? ''],
            }),
            template_details: this.formBuilder.group({
                eform_message: [data.template_details?.eform_message ?? ''],
                visible_name: [data.template_details?.visible_name ?? data.template.name],
                save_to_folder: [data.template_details?.save_to_folder ?? 0],
                folders_id: [data.template_details?.folders_id ?? null],
                allow_chat: [data.template_details?.allow_chat ?? 0],
                attach_video: [data.template_details?.attach_video ?? 0],
                video_type: [data.template_details?.video_type ?? 'youtube'],
                video_url: [data.template_details?.video_url ?? ''],
                video_title: [data.template_details?.video_title ?? ''],
                status: [data.template_details?.status ?? '0'],
                add_to_contract_card: [false],
                selected_categories: this.formBuilder.array(data.template_details?.selected_categories ?? []),
                type: [data.template_details?.type ?? TemplateType.REGULAR],
            }),
            template_settings: this.formBuilder.group({
                convert_to_eform: [data.template_settings?.convert_to_eform ?? 0],
                description: [data.template_settings?.description ?? "", Validators.maxLength(200)],
            }),
            convert_to_eform_settings: this.convertToEformSettingsFormGroup(data),
            template_roles: this.formBuilder.array(this.getRoles(data.template_roles), this.uniqueSequentialInvitationOrderValidator(data.template_details?.type ?? TemplateType.REGULAR)),
            deleted_roles: this.formBuilder.array([]),
            deleted_extra_fields: this.formBuilder.array([]),
            top_section: this.formBuilder.group({}),
            signature: this.formBuilder.group({}),
            details: this.formBuilder.group({}),
            template_owner: this.formBuilder.group({
                id: [data.template_owner?.id ?? null],
                name: [data.template_owner?.name ?? ''],
                email: [data.template_owner?.email ?? ''],
                changed: [false]
            })
        });
    }



  
    convertToEformSettingsFormGroup(data:TemplateForm){
        const URL_PATTERN = /^(https?:\/\/)((([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})|localhost)(:\d+)?(\/.*)?$/;
        return this.formBuilder.group({
            id: [data?.convert_to_eform_settings?.id ?? null],
            redirect_url_setting: [data?.convert_to_eform_settings?.redirect_url_setting ?? 0],
            redirect_url: [data?.convert_to_eform_settings?.redirect_url ?? '', Validators.pattern(URL_PATTERN)],
            template_id: [data?.template?.id ?? null],
            participant: [data?.convert_to_eform_settings?.participant ?? 0],
            backround: [data?.convert_to_eform_settings?.backround ?? 0],
            arrows: [data?.convert_to_eform_settings?.arrows ?? 0],
            users: this.formBuilder.array(this.eformUsers(data?.convert_to_eform_settings?.user_convert_to_eform_settings ?? [])),
            unique_id: [data?.convert_to_eform_settings?.unique_id ?? ''],
            logo_type: [data?.convert_to_eform_settings?.logo_type ?? LogoTypeEnum.no_logotype],
            title_settings: [data?.convert_to_eform_settings?.title_settings ?? 0],
            title: [data?.convert_to_eform_settings?.title ?? null],
            description_settings: [data?.convert_to_eform_settings?.description_settings ?? 0],
            description: [data?.convert_to_eform_settings?.description ?? null],
            term_and_condition_settings: [data?.convert_to_eform_settings?.term_and_condition_settings ?? 0],
            term_and_condition_content: [data?.convert_to_eform_settings?.term_and_condition_content ?? null],
            brand_color: [data?.convert_to_eform_settings?.brand_color ?? '#0060ed'],
            eform_steps: this.formBuilder.array(this.eformSteps(data?.convert_to_eform_settings?.eform_steps ?? steps())),
        })
    }
        

    /**
     * Prepares a form group for sending a document based on the provided template form data.
     * 
     * @param data - The template form data used to populate the form group.
     * @returns A promise that resolves to a FormGroup with the structure required for sending a document.
     * 
     * The form group structure includes:
     * - `template`: A nested form group containing template details such as id, template_id, name, and content.
     * - `template_details`: A nested form group containing various details about the template such as visible_name, language, signing_deadline, and more.
     * - `template_roles`: A form array containing roles associated with the template.
     * - `deleted_roles`: A form array for tracking deleted roles.
     * - `deleted_extra_fields`: A form array for tracking deleted extra fields.
     */
    private prepairSendDocumentForm(data: TemplateForm) {
        this.templateForm = data;
        return this.formBuilder.group({
            template: this.formBuilder.group({
                id: [data?.template?.id ?? 0],
                template_id: [data?.template?.template_id],
                template_name: [data?.template?.template_name], // when user select template for send document
                name: [data?.template?.name ?? '', [Validators.required]],
                content: [data?.template?.content ?? ''],
            }),
            template_details: this.formBuilder.group({
                visible_name: [data.template_details?.visible_name ?? data?.template?.name],
                save_to_folder: [data.template_details?.save_to_folder ?? 0],
                folders_id: [data.template_details?.folders_id ?? null],
                attach_video: [data.template_details?.attach_video ?? 0],
                video_type: [data.template_details?.video_type ?? 'youtube'],
                video_url: [data.template_details?.video_url ?? ''],
                video_title: [data.template_details?.video_title ?? ''],
                status: [data.template_details?.status ?? 'initiator'],
                add_to_contract_card: [false],
                selected_categories: this.formBuilder.array(data.template_details?.selected_categories ?? []),
                type: [data.template_details?.type ?? TemplateType.REGULAR],
            }),
            template_settings: this.formBuilder.group({}),
            template_roles: this.formBuilder.array(this.getRoles(data.template_roles, true), [this.requireAtleastOneRole(), this.singleSameDeviceValidator()]),
            deleted_roles: this.formBuilder.array([]),
            deleted_extra_fields: this.formBuilder.array([]),
            top_section: this.formBuilder.group({}),
            signature: this.formBuilder.group({}),
            details: this.formBuilder.group({}),
        });
    }


    
  
    /**
     * Generates a FormGroup array for the given roles with specified validation rules.
     *
     * @param roles - An array of RecipientRole objects to generate the form groups for.
     * @param fieldRequired - A boolean indicating if the recipient_id field is required. Defaults to false.
     * @returns An array of FormGroup objects representing the roles.
     */
    getRoles(roles: RecipientRole[], fieldRequired: boolean = false, isEForm: boolean = false) {
        return roles.map(role => this.getRole(role, fieldRequired, isEForm));
    }


    /**
     * Creates and returns a FormGroup representing a recipient role with pre-filled values and validation.
     *
     * @param role - The recipient role object containing initial values for the form controls.
     * @param fieldRequired - Optional. If true, certain fields (e.g., recipient_id) are marked as required. Defaults to false.
     * @param isEForm - Optional. If true, disables required validation for some fields. Defaults to false.
     * @returns A FormGroup instance with controls for role properties, including nested arrays for personfields and orgfields.
     */
    getRole(role: RecipientRole, fieldRequired: boolean = false, isEForm: boolean = false){
       return this.formBuilder.group(
            {
                id: [role.id ?? 0],
                recipient_id: [role.recipient_id ?? null, (!isEForm && fieldRequired) ? [Validators.required] : []],
                organization_id: [role.organization_id ?? null],
                role_name: [role.role_name ?? `Recipient 1`],
                role_color: [role.role_color ?? this.globalService.generateRandomColor()],
                sender_as_role: [role.sender_as_role ?? 0],
                invitation_type: [role.invitation_type ?? 'email'],
                invitation_order: [role.invitation_order ?? 1],
                authentication_type: [role.authentication_type ?? 'e_signature'],
                authentication_required: [role.authentication_required ?? true],
                confirmation_type: [role.confirmation_type ?? 'email'],
                signature_confirmation: [role.signature_confirmation ?? ''],
                personfields: this.formBuilder.array(this.getPersonfields(role.personfields, role.recipient, fieldRequired)),
                orgfields: this.formBuilder.array(this.getOrgfields(role.orgfields, role.organization, fieldRequired)),
                recipient: [this.skipKeys(role.recipient) ?? null],
                organization: [this.skipKeys(role.organization) ?? null],
                lock: [role.lock ?? 0],
                allow_to_read: [role.allow_to_read ?? false],
                allow_to_forward: [role.allow_to_forward ?? false],
                allow_to_invite: [role.allow_to_invite ?? false],
            }
        )
    }



   
    /**
     * Retrieves and processes person fields for a given recipient.
     *
     * @param personfieldsdata - Partial array of Personfield objects containing the data to be processed.
     * @param recipient - The recipient for whom the fields are being retrieved.
     * @param fieldRequired - A boolean indicating whether the field is required.
     * @param manipulateRequest - A boolean indicating whether to manipulate the request data. Defaults to true.
     * @param is_enable - An integer indicating whether the field is enabled. Defaults to 0.
     * @returns An array of FormGroup objects representing the processed person fields.
     */
    getPersonfields(personfieldsdata: Partial<Personfield[]>, recipient: Recipient, fieldRequired: boolean, manipulateRequest = true, is_enable = 0) {
        const fields = personFields(recipient, this.globalService, is_enable);
        let extraFields = [];
        if (manipulateRequest && personfieldsdata?.length) {
            personfieldsdata.forEach((l) => {
                const found = fields.find((v) => v.name == l.name);
                if (found && l.value != '') {
                    const updatedField = { ...l, value: (found.value ? found.value : l.value) };
                    extraFields.push(updatedField);
                } else {
                    extraFields.push(l);
                }
            });
        } else {
            extraFields = fields;
        }
        
        // populateLastName is the temrarory function becuase we need to remove the firstname and lastname feature
        extraFields = this.populateLastName(extraFields);
        return extraFields.map((extraField: IExtraFiledFormInterface) => this.formBuilder.group(
            {
                id: [extraField.id ?? null],
                key: [String(extraField.name).replace(/[^A-Za-zÀ-ÖØ-öø-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻż0-9.,?!@#&()\-'"%;:^$]/g, '_').toLowerCase()],
                is_enable: [this.setValidationFormDocumentKey(extraField, 'is_enable')],
                name: [extraField.name ?? ''],
                translation: [extraField.is_default == 1 ? `${extraField.name} (general) - ${extraField.name}` : extraField.name],
                hidden: 0,
                value: [extraField.value, this.applyValidation(fieldRequired, extraField)],
                required: [this.setValidationFormDocumentKey(extraField, 'required')],
                validation: [extraField.validation],
                hint: [extraField?.hint],
                metadata_id: [extraField?.metadata_id],
                metadata_type: [extraField?.metadata_type],
                is_default: [extraField.is_default],
                validations: [(extraField).validations || {}],
                validation_title: [this.globalService.getValidationTitle((extraField).validations)],
                lock: [extraField?.lock ?? 0],
                landcode: [extraField?.landcode ?? null]
            }
        ));
    }




    
    /**
     * Generates a FormGroup array for organization fields with specified validation rules.
     *
     * @param orgfields - An array of Orgfield objects containing the data to be processed.
     * @param organization - The organization for which the fields are being retrieved.
     * @param fieldRequired - A boolean indicating whether the field is required.
     * @param manipulateRequest - A boolean indicating whether to manipulate the request data. Defaults to true.
     * @param is_enable - An integer indicating whether the field is enabled. Defaults to 0.
     * @returns An array of FormGroup objects representing the processed organization fields.
     */
    getOrgfields(orgfields: Orgfield[], organization: Organization, fieldRequired: boolean, manipulateRequest = true, is_enable = 0) {
        const orgFields = organizationFields(organization, this.globalService, is_enable);
        let extraFields = [];
        if (manipulateRequest && orgfields?.length) {
            orgfields.forEach((l) => {
                const found = orgFields.find((v) => v.name == l.name);
                if (found && l.value != '') {
                    const updatedField = { ...l, value: (found.value ? found.value : l.value) };
                    extraFields.push(updatedField);
                } else {
                    extraFields.push(l);
                }
            });
        } else {
            extraFields = orgFields;
        }
        const data = extraFields.map(orgfield => this.formBuilder.group(
            {
                id: [orgfield.id ?? null],
                key: [String(orgfield.name).replace(/[^A-Za-zÀ-ÖØ-öø-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻż0-9.,?!@#&()\-'"%;:^$]/g, '_').toLowerCase()],
                is_enable: [orgfield.is_enable],
                name: [orgfield.name ?? ''],
                translation: [orgfield.is_default == 1 ? `${orgfield.name} (general) - ${orgfield.name}` : orgfield.name],
                value: [orgfield.value, this.applyValidation(fieldRequired, orgfield)],
                required: [orgfield.required],
                validation: [orgfield.validation],
                is_default: [orgfield.is_default],
                validations: [orgfield.validations || {}],
                hint: [orgfield?.hint],
                metadata_id: [orgfield?.metadata_id],
                metadata_type: [orgfield?.metadata_type],
                validation_title: [this.globalService.getValidationTitle(orgfield.validations)],
                lock: [orgfield?.lock ?? 0],
                landcode: [null]
            }
        ));
        return data;
    }




    /**
     * Processes an array of person fields to adjust name-related fields.
     *
     * This function identifies fields with names "Full name" and "Last name" within
     * the provided array of `Personfield` objects. If both fields are present, it
     * renames the "Full name" field to "First name", splits its value into parts,
     * and assigns the last part as the value of the "Last name" field. If the
     * "Full name" field has only one part, the "Last name" field is assigned an
     * empty string. If the "Full name" field is absent, it checks for a "First name"
     * field and trims its value.
     *
     * @param fields - An array of `Personfield` objects to be processed.
     * @returns A new array of `Personfield` objects with updated name fields.
     */  
    populateLastName(fields: Personfield[]): Personfield[] {
        const cloneField = [...fields];
        // Find the "Full name" and "Last name" objects
        const fullNameField = cloneField.find(field => (field.name === "Full name"));
        const lastNameField = cloneField.find(field => field.name === "Last name");
        if (fullNameField && lastNameField) {
            //rename the fullname into the first name
            fullNameField.name = "First name";
            // Split the full name into parts
            const nameParts = String(fullNameField.value ?? '').trim().split(" ");
            if (nameParts && nameParts.length > 1 && fullNameField.value) {
                // Assign the last part as the last name
                fullNameField.value = nameParts.slice(0, -1).join(' ');
                lastNameField.value = nameParts.slice(-1).join(" "); // Handles multi-part last names
            } else {
                // If only one part exists, assign an empty string to the last name
                lastNameField.value = "";
            }
        } else {
            const firstNameField = cloneField.find(field => (field.name === "First name"));
            if (firstNameField && firstNameField?.value) {
                const nameParts = String(firstNameField.value).trim();
                firstNameField.value = nameParts ?? "";
            }
        }
        return cloneField;
    }


    /**
     * Initializes and adds a new role for a template or document based on the route.
     *
     * This method selects the appropriate service and parameters based on the given
     * route and invokes the `addTemplateRole` method of the selected service. It
     * constructs a new role using the `addNewRole` method with specified parameters.
     *
     * @param route - A string representing the current route, which determines the service to use.
     * @param id - An identifier for the document or template.
     * @param roleNo - The role number to be added.
     * @param templateForm - The template form data to be associated with the role.
     * @returns - The result of adding the new role using the selected service.
     */
    initAddNewRole(route: string, id: number, roleNo: number, templateForm: TemplateForm) {
        this.templateForm = templateForm;
        const routeConfig = {
            'send-document': {
                service: this.documentsService,
                key: 'document_id',
                extraParam: true
            },
            'edit-template': {
                service: this.editTemplateService,
                key: 'template_id',
                extraParam: undefined
            }
        };
        
        if (routeConfig[route]) {
            const { service, key, extraParam } = routeConfig[route];
            return service.addTemplateRole(this.addNewRole(key, id, roleNo, extraParam));
        }
    }

    /**
     * add new role form
     * @param key
     * @param id
     * @param roleNo
     * @param fieldRequired
     * @param recipient
     * @param roleName
     * @param is_enable
     */
    addNewRole(key: string, id: number, roleNo: number, fieldRequired: boolean = false, recipient: Recipient = null, roleName: string = "Recipient", is_enable = 0): RecipientRole {
        const newRole = this.formBuilder.group({
            [key]: [id],
            role_name: [`${roleName}${roleNo ? ' ' + roleNo : ''}`],
            role_color: this.globalService.generateRandomColor(),
            sender_as_role: [0],
            recipient_id: [recipient ? recipient.id : null, fieldRequired ? [Validators.required] : []],
            organization_id: [recipient ? recipient.organization_id : null],
            invitation_type: ['email'],
            invitation_order: [1],
            authentication_type: ['e_signature'],
            authentication_required: [true],
            confirmation_type: ['email'],
            signature_confirmation: [''],
            personfields: this.formBuilder.array(this.getPersonfields([], recipient || {}, fieldRequired, false, is_enable)),
            orgfields: this.formBuilder.array(this.getOrgfields([], recipient ? recipient.organization : {}, fieldRequired, false, is_enable)),
            recipient: recipient || null,
            organization: recipient ? recipient.organization : null
        });
        return newRole.value;
    }

    



    /**
     * Apply validations to the form fields.
     * @param fieldRequired - A boolean indicating whether the fields are required.
     * @param fields - The fields to be validated.
     * @returns The validation rules to be applied.
     */
    applyValidation(fieldRequired, fields: IExtraFiledFormInterface) {
        if (fieldRequired) {
            return this.globalService.setValidationsOnRequiredFields(fields);
        } else {
            return this.globalService.setValidationsOnFields(fields);
        }
    }



    /**
     * Checking is send document route
     * @param {route}
     * @returns {boolean}
     */
    isSendDocument = (route: string) => (route === 'send-document');




    /**
     * Removes specific keys from a Recipient object.
     * 
     * @param object - The Recipient object to process.
     * @returns A new object with `created_at` and `updated_at` keys removed, or null if the input is null or undefined.
     */
    skipKeys(object: Recipient) {
        if (object) {
            const processedObject = { ...object };
            delete processedObject.created_at;
            delete processedObject.updated_at;
            return processedObject;
        }
        return null;
    }

    


    /**
     * Converts an array of user data into an array of FormGroup objects.
     *
     * This function maps each user object to a FormGroup using Angular's FormBuilder.
     * Each FormGroup contains user-specific fields such as user_id, 
     * convert_to_eform_setting_id, name.
     *
     * @param users - An array of user objects containing details for each user.
     * @returns An array of FormGroup objects representing the provided user data.
     */
    eformUsers(users) {
        return users.map(user => this.formBuilder.group(
            {
                user_id: user.user_id,
                convert_to_eform_setting_id: user.convert_to_eform_setting_id,
                name: user?.user?.name,
                unique_id: user?.unique_id
            }
        ));
    }

    /** set the eform steps
     */
    eformSteps(steps:Steps[]) {
        return steps.map(step => this.formBuilder.group(
            {
                id: step.id,
                convert_to_eform_setting_id: step.convert_to_eform_setting_id,
                heading: step?.heading,
                subheading: step?.subheading,
                key: step?.key,
                order: step?.order,
                status: step?.status,
                editingHeading: false,
                editingSubheading: false
            }
        ));
    }

    setValidationFormDocumentKey(personfield: IExtraFiledFormInterface, key: string) {
        const isDocumentKeyValid = this.DK && this.templateForm?.template_details?.document_key === 1;
        if (isDocumentKeyValid) {
            const { dk_type, dk_send_via_sms } = this.templateForm?.template_details || {};
            const { name } = personfield;
            if (((dk_type === "pin_code" || (dk_type == "password" && dk_send_via_sms === 1)) && name === "Phone no") || (dk_type === "bank_id" && name === "Personal ID number")) {
                return 1;
            }
        }
        return personfield[key];
    }

    async updateDateFormatInDatePicker() {
        const fields_data = this.globalService.fields_data
        for (const key in fields_data) {
            const field = fields_data[key];
            if (field && field?.type == "datepicker" && field?.value) {
                const value = await lastValueFrom(<Observable<string>>this._dateFormatPipe$.transform(field?.value, {} as UserTimezone));
                this.globalService.updateDatePickerTagFormat(field?.uuid, value);
            }
        }
    }

    /**
    * required at-least one role before sending the document
    */
    requireAtleastOneRole() {
        const requireAtleastOneRoleroleTranslation = this.translocoService$.translate("Toaster error message for sending document without role (Send Document section) - Please add atleast one role before sending document");
        return (formGroup: FormArray) => {
            if (formGroup.value.length == 0) {
                return { error: requireAtleastOneRoleroleTranslation }
            }
            const orderNumbers = formGroup.value.map((item) => item.invitation_order);
            let maximum = 0;
            for (let i = 0; i < orderNumbers.length; i++) {
                maximum = orderNumbers[i] > maximum ? orderNumbers[i] : maximum;
            }
            for (let i = 1; i <= maximum; i++) {
                let checkForOrder = false;
                orderNumbers.forEach(order => {
                    if (order == i) {
                        checkForOrder = true;
                    }
                });
                if (checkForOrder == false) {
                    return { invalidOrderSequence: true };
                }
            }
            return null;
        }
    }

    /**
     * Updates the phone number validation for the given template roles.
     *
     * This method iterates through the `personfields` FormArray within the provided
     * `template_roles` FormGroup and applies validation rules to the field with the key `phone_no`.
     * Specifically, it sets the `required` property and adds/removes the `Validators.required`
     * validator to the `value` control of the `phone_no` field.
     *
     * @param template_roles - The FormGroup containing the `personfields` FormArray.
     * @param required - A boolean indicating whether the phone number field should be required.
     */
    setPhoneNumberValidation(template_roles, required: boolean) {
        const personFields = template_roles.get('personfields') as FormArray;
        personFields.value.forEach((field: IExtraFiledFormInterface, itemIndex: number) => {
            if(field.key === 'phone_no'){
                const phone_no = personFields.get(`${itemIndex}`);
                phone_no.patchValue({required: required});
                phone_no.get('value').setValidators(required ? Validators.required : null);
                phone_no.get('value').updateValueAndValidity();
            }
        })
    }

    uniqueSequentialInvitationOrderValidator(templateType: TemplateType): ValidatorFn {
        return (formArray: AbstractControl): ValidationErrors | null => {
            if (!(formArray instanceof FormArray)) return null;

            if (templateType !== TemplateType.EFORM) return null;

            const orders = formArray.controls.map(control =>
                Number(control.get('invitation_order')?.value)
            );

            const hasDuplicates = new Set(orders).size !== orders.length;

            const isSequential = orders
            .slice() // shallow copy
            .sort((a, b) => a - b)
            .every((val, index) => val === index + 1);

            if (hasDuplicates || !isSequential) {
                return { invalidInvitationOrder: true };
            }

            return null;
        };
    }

    singleSameDeviceValidator(): ValidatorFn {
        return (formArray: AbstractControl): ValidationErrors | null => {
            if (!(formArray instanceof FormArray)) return null;

            const sameDeviceRoles = formArray.controls.filter(control =>
                control.get('invitation_type')?.value === InvitationThroughEnum.SAME_DEVICE
            );

            // More than one 'Same device' role is not allowed.
            if (sameDeviceRoles.length > 1) {
                return { multipleSameDeviceNotAllowed: true };
            }

            // The 'Same device' role must be the first in order.
            if (sameDeviceRoles.length === 1 && sameDeviceRoles[0].get('invitation_order')?.value !== 1) {
                return { sameDeviceMustBeFirst: true };
            }

            return null;
        };
    }

    /**
     * Updates the `landcode` field in the provided `personfieldsForm` FormGroup.
     * 
     * If the form's value contains an `e164Number`, the `landcode` is set to the lowercase string
     * of the form's `countryCode` value. Otherwise, it uses the lowercase version of the `iso2`
     * property from the `$event` parameter.
     *
     * @param $event - An object that may contain an `iso2` country code string.
     * @param personfieldsForm - The FormGroup representing person fields to be updated.
     */
    updateTheLandCode($event: { iso2?: string }, personfieldsForm: FormGroup) {
        const personfieldsValue = personfieldsForm.value;
        if (personfieldsValue?.value?.e164Number) {
            const country = personfieldsValue?.value?.countryCode ? String(personfieldsValue.value.countryCode).toLowerCase() : null;
            personfieldsForm.patchValue({
                landcode: country
            });
        } else {
            const country = $event?.iso2 ? String($event.iso2).toLowerCase() : null;
            personfieldsForm.patchValue({
                landcode: country
            });
        }
    }

}
